/**
 * Live Copilot — LA fenêtre de l'appel, en semi plein écran.
 *
 * Quand l'appel se lance, une seule fenêtre s'ouvre et prend l'essentiel de
 * l'écran. Trois colonnes, trois rôles :
 *
 *   - à gauche, les actions rapides : planifier un rendez-vous, une démo, un
 *     rappel, un email — les fonctions qui existent déjà dans l'application
 *     (PlanActionModal), accessibles sans changer de fenêtre ;
 *   - au milieu, l'appel lui-même : le discours d'ouverture défini dans la
 *     préparation, épinglé au-dessus de la conversation jusqu'à la fin de la
 *     lecture, puis la transcription et les suggestions en direct ;
 *   - à droite, les questions et informations utilisables pendant l'appel :
 *     répliques préparées avant l'appel, réponses aux objections éprouvées
 *     par l'équipe, mémoire du prospect.
 *
 * Sur écran étroit, les colonnes latérales s'effacent : le milieu est l'appel,
 * il ne se sacrifie jamais.
 *
 * La décision reste au commercial : l'IA suggère, elle ne parle jamais au
 * client et n'envoie rien à sa place. Réduire n'interrompt rien — la
 * transcription et l'analyse continuent côté serveur (crm/components/
 * FloatingWindow). Un appel sans un mot transcrit n'a pas de compte rendu :
 * le panneau le dit, au lieu d'annoncer une analyse qui ne viendra pas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/crmApi";
import {
  Sparkles, Check, RefreshCw, Lightbulb, PhoneCall, AlertTriangle,
  ChevronDown, MessageSquare, Users, MonitorPlay, RotateCcw, Mail,
  ExternalLink, Brain, BookOpen, ChevronsUpDown, ChevronsDownUp,
} from "lucide-react";
import FloatingWindow from "@/crm/components/FloatingWindow";
import PlanActionModal from "@/crm/components/PlanActionModal";
import useCopilotSocket from "./useCopilotSocket";

const TYPE_STYLE = {
  QUESTION: ["Question", "bg-[#eeecff] text-[#4a3ae0]"],
  RESPONSE: ["Réponse", "bg-[#e6f1fb] text-[#1f5f9e]"],
  OBJECTION_HANDLING: ["Objection", "bg-[#fdeceb] text-[#d64a3a]"],
  ARGUMENT: ["Argument", "bg-[#e6f5ec] text-[#1f7a48]"],
  CLARIFICATION: ["Clarifier", "bg-[#f1f2f7] text-[#61667a]"],
  NEXT_STEP: ["Étape suivante", "bg-[#fdf3dd] text-[#8a6b1f]"],
  WARNING: ["Vigilance", "bg-[#fdeceb] text-[#d64a3a]"],
  OPPORTUNITY: ["Opportunité", "bg-[#e6f5ec] text-[#1f7a48]"],
};

// Repli si le playbook est injoignable : le mode manuel doit rester disponible
// même quand la configuration ne l'est pas — c'est le dernier recours du
// commercial pendant un appel.
const FALLBACK_INTENTS = [
  { id: "question", label: "Suggérer une question" },
  { id: "objection", label: "Répondre à cette objection" },
  { id: "conclure", label: "Comment conclure ?" },
  { id: "resumer", label: "Résumer la situation" },
  { id: "argument", label: "Trouver un argument" },
];

// Actions rapides de la colonne de gauche — les mêmes fonctions que partout
// ailleurs dans l'application, juste à portée de main pendant l'appel.
const QUICK_ACTIONS = [
  { kind: "MEETING", label: "Rendez-vous", icon: Users },
  { kind: "DEMO", label: "Démo", icon: MonitorPlay },
  { kind: "FOLLOW_UP", label: "Rappel", icon: RotateCcw },
  { kind: "EMAIL", label: "Email", icon: Mail },
];

const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Phrase de temporisation par défaut (plan « Zéro temps mort », Lot 0) : à
// dire pendant que le copilote cherche, pour que le client n'entende jamais de
// blanc. La banque de réponses préparées en fournit une adaptée à l'offre dès
// qu'elle est prête ; celle-ci sert avant.
const TEMPO_DEFAULT = "Je vous explique en deux mots, vous me direz si c'est pertinent pour vous.";

function AsideTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 px-1 font-mono text-[10px] uppercase tracking-[.12em] text-[#8a90a3]">
      {Icon && <Icon size={12} />} {children}
    </div>
  );
}

export default function LiveCopilot({ sessionId, live = true, answered = true, callSeconds = 0,
                                      companyName, companyId, objective, onClose }) {
  const navigate = useNavigate();
  const {
    connected, transcript, partial, suggestion, thinking, warning, analysis,
    prepared, autoSuggest, autoSuggestLocked, setAuto, send, dismissSuggestion,
    disconnect,
  } = useCopilotSocket(sessionId, { enabled: Boolean(sessionId) });
  const [menuOpen, setMenuOpen] = useState(false);
  // Le menu « Suggérer » liste le playbook de l'équipe, dans l'ordre de
  // priorité configuré : demander manuellement un angle, c'est choisir une
  // règle du moteur — pas une intention parallèle codée en dur.
  const [intents, setIntents] = useState(FALLBACK_INTENTS);
  // Règle en tête du playbook : sert à nommer ce que le mode automatique suit.
  const [topRule, setTopRule] = useState("");
  // Préparation de l'appel : le discours d'ouverture et la mémoire du prospect
  // viennent de « Préparer mon appel » (même endpoint) — la fenêtre d'appel
  // les affiche elle-même, plus besoin d'une seconde fenêtre à côté.
  const [prep, setPrep] = useState(null);
  // Le discours reste épinglé au-dessus de la conversation tant que le
  // commercial n'a pas dit qu'il avait fini de le lire.
  const [scriptOpen, setScriptOpen] = useState(true);
  // Fiche complète de l'entreprise — nécessaire aux actions rapides
  // (PlanActionModal attend la fiche, pas seulement un identifiant).
  const [company, setCompany] = useState(null);
  const [planKind, setPlanKind] = useState(null);
  // Réponses éprouvées de la bibliothèque d'objections, pour la colonne droite.
  const [objections, setObjections] = useState([]);
  // Questions et objections repliées par défaut : on lit un titre, on ouvre
  // ce qu'on cherche. « Tout déplier » les ouvre d'un geste ; `epoch` remonte
  // les <details> pour que la bascule s'impose aussi à ceux touchés à la main.
  const [allOpen, setAllOpen] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const toggleAll = () => { setAllOpen((v) => !v); setEpoch((n) => n + 1); };
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/copilot/playbook")
      .then(({ data }) => {
        const manual = (data.manual || []).map((r) => ({ id: r.id, label: r.label }));
        if (manual.length) setIntents(manual);
        setTopRule((data.rules || [])[0]?.label || "");
      })
      .catch(() => { /* repli conservé */ });
    api.get("/copilot/objections")
      .then(({ data }) => setObjections((data.objections || [])
        .filter((o) => o.status === "approved" && o.response).slice(0, 8)))
      .catch(() => { /* colonne simplement vide */ });
  }, []);

  // Un nouvel appel = une nouvelle préparation : discours, mémoire, fiche.
  useEffect(() => {
    setPrep(null); setScriptOpen(true); setCompany(null); setPlanKind(null);
    if (!companyId) return;
    api.post(`/copilot/prepare/${companyId}`, { objective: objective || "" })
      .then(({ data }) => setPrep({
        script: data.discourse?.final_text || "",
        memory: data.client_memory || null,
        solution: data.solution || null,
      }))
      .catch(() => { /* appel sans préparation : le milieu vit très bien seul */ });
    api.get(`/companies/${companyId}`)
      .then(({ data }) => setCompany(data))
      .catch(() => { /* actions rapides simplement indisponibles */ });
  }, [sessionId, companyId, objective]);

  // Le fil doit rester collé au bas : pendant un appel, personne ne fait défiler.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript, partial, suggestion]);

  const lines = useMemo(
    () => (partial ? [...transcript, { ...partial, __partial: true }] : transcript),
    [transcript, partial],
  );

  // Appel terminé sans un mot transcrit : il n'y a pas eu de conversation,
  // donc rien à analyser — le panneau n'annonce pas de compte rendu. Deux cas :
  // jamais décroché (pas de réponse, échec), où plus rien ne peut venir et où
  // l'on coupe le canal — sinon il se reconnectait sans fin à une session que
  // le serveur n'a jamais alimentée ; décroché mais muet, où le canal reste
  // ouvert : une parole transcrite tardivement rend la main au flux normal.
  const silent = !live && lines.length === 0;
  const noCall = silent && !answered;
  useEffect(() => { if (noCall) disconnect(); }, [noCall, disconnect]);

  const useSuggestion = async () => {
    try {
      await navigator.clipboard.writeText(suggestion.text);
      toast.success("Suggestion copiée");
    } catch {
      toast.message(suggestion.text, { duration: 8000 });
    }
    send("suggestion_used", { suggestion_id: suggestion.id });
    dismissSuggestion();
  };

  const ignore = () => {
    send("suggestion_ignored", { suggestion_id: suggestion.id });
    dismissSuggestion();
  };

  const [typeLabel, typeClass] = TYPE_STYLE[suggestion?.type] || TYPE_STYLE.QUESTION;

  const status = !live ? (noCall ? "Appel non abouti" : "Appel terminé")
    : !connected ? "Reconnexion…"
    : autoSuggest ? "Copilote actif"
    : autoSuggestLocked ? "Copilote en manuel (réglage administrateur)" : "Copilote en manuel";

  const script = prep?.script || "";
  const memory = prep?.memory;
  const known = (memory?.key_points || []).length > 0 || Boolean(memory?.summary);

  return (
    <FloatingWindow
      id="live-copilot" testid="live-copilot" side="center" width={1280} maxWidth={1480}
      tone="green"
      /* Un nouvel appel remet le copilote en avant, même s'il avait été rangé
         pendant le précédent. */
      restoreKey={sessionId}
      icon={PhoneCall} accent="text-[#2f9e5c]" accentDark="text-emerald-400"
      title={companyName || "Appel en cours"}
      subtitle={
        <span className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${live && connected ? "bg-[#2f9e5c]" : "bg-[#d3d7e1]"}`} />
          {status}
        </span>
      }
      note={fmtClock(callSeconds)}
      aside={<span className="shrink-0 font-mono text-[13px] text-[#61667a]">{fmtClock(callSeconds)}</span>}
      onClose={onClose}
      closeTitle={live ? "Masquer le copilote" : silent ? "Fermer" : "Fermer le compte rendu"}
    >
      <div className="flex h-[calc(100vh-13rem)] min-h-[380px]">

        {/* ══ GAUCHE — actions rapides (les fonctions existantes, sans changer
            de fenêtre). Sur écran étroit, la colonne s'efface. */}
        <aside className="hidden w-[190px] shrink-0 flex-col gap-3 overflow-y-auto border-r border-[#eef0f4] bg-[#fafbfc] p-3 lg:flex">
          <AsideTitle>Actions rapides</AsideTitle>
          <div className="flex flex-col gap-1.5">
            {QUICK_ACTIONS.map(({ kind, label, icon: Icon }) => (
              <button key={kind} data-testid={`quick-${kind}`} disabled={!company}
                onClick={() => setPlanKind(kind)}
                title={company ? `Planifier — ${label}` : "Aucune fiche liée à cet appel"}
                className="flex items-center gap-2 rounded-xl border border-[#e6e8ee] bg-white px-3 py-2
                           text-[13px] text-[#4c5164] hover:border-[#b9b1ff] hover:text-[#3a2cc8]
                           disabled:cursor-not-allowed disabled:opacity-45">
                <Icon size={14} className="shrink-0 text-[#8a90a3]" /> {label}
              </button>
            ))}
          </div>
          {companyId && (
            <>
              <div className="border-t border-[#eef0f4]" />
              <button onClick={() => navigate(`/entreprises/${companyId}`)}
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-[12.5px] text-[#61667a] hover:bg-white hover:text-[#16181d]">
                <ExternalLink size={13} className="shrink-0 text-[#8a90a3]" /> Ouvrir la fiche
              </button>
            </>
          )}
          {prep?.solution?.nom && (
            <div className="mt-auto rounded-xl bg-[#e6f1fb] px-3 py-2.5">
              <div className="font-mono text-[9.5px] uppercase tracking-[.1em] text-[#6b93b8]">Solution proposée</div>
              <div className="mt-0.5 text-[12.5px] leading-snug text-[#1f5f9e]">{prep.solution.nom}</div>
            </div>
          )}
        </aside>

        {/* ══ MILIEU — l'appel : discours épinglé, transcription, suggestion. */}
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-[#eef0f4] bg-white/95 px-4 py-2 text-[11.5px] text-[#71768a]">
            {objective && <span className="min-w-0 truncate">Objectif : <span className="text-[#4c5164]">{objective}</span></span>}
            {script && !scriptOpen && live && (
              <button onClick={() => setScriptOpen(true)}
                className="ml-auto shrink-0 font-medium text-[#4a3ae0] hover:underline">
                Relire le discours
              </button>
            )}
          </div>

          {/* LE DISCOURS D'OUVERTURE — épinglé au-dessus de la conversation,
              fixe pendant que la transcription défile dessous, jusqu'à ce que
              le commercial dise qu'il a terminé sa lecture. */}
          {script && scriptOpen && live && (
            <div data-testid="call-script" className="shrink-0 border-b border-[#e8e4f8] bg-[#f8f7ff] px-4 py-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[.1em] text-[#4a3ae0]">
                  Discours d'ouverture — à lire tel quel
                </span>
                <button data-testid="script-done" onClick={() => setScriptOpen(false)}
                  className="flex shrink-0 items-center gap-1 rounded-lg border border-[#b9b1ff] px-2 py-1 text-[11.5px] font-medium text-[#4a3ae0] hover:bg-[#eeecff]">
                  <Check size={12} /> Lecture terminée
                </button>
              </div>
              <p className="max-h-[26vh] overflow-y-auto whitespace-pre-wrap text-[14px] leading-relaxed text-[#16181d]">
                {script}
              </p>
            </div>
          )}

          {/* TRANSCRIPTION — second plan assumé (Lot 2) : elle se consulte,
              elle ne se lit pas pendant qu'on parle. */}
          <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {lines.length === 0 && (
              <p className="py-8 text-center text-[13px] text-[#9aa0b2]">
                {live ? "La transcription apparaîtra dès les premiers mots."
                  : noCall ? "L'appel n'a pas abouti — pas de conversation, donc pas de compte rendu."
                  : "Aucune parole n'a été transcrite pendant cet appel — pas de compte rendu."}
              </p>
            )}
            {lines.map((line, i) => {
              const isCustomer = line.speaker === "customer";
              return (
                <div key={`${line.timestamp_ms}-${i}`} className={line.__partial ? "opacity-50" : ""}>
                  <div className={`mb-0.5 font-mono text-[9.5px] uppercase tracking-[.1em] ${isCustomer ? "text-[#6b93b8]" : "text-[#b3b8c7]"}`}>
                    {isCustomer ? "Client" : "Commercial"}
                  </div>
                  <div className={`rounded-lg px-2.5 py-1.5 text-[12.5px] leading-relaxed ${isCustomer ? "bg-[#f4f7fb] text-[#4c5164]" : "bg-[#f7f8fa] text-[#71768a]"}`}>
                    {line.text}
                  </div>
                </div>
              );
            })}

            {thinking && (
              <div className="flex items-center gap-2 text-[12.5px] text-[#8a90a3]">
                <RefreshCw size={13} className="animate-spin" />
                {thinking?.live
                  ? "Rédaction en direct — gardez la parole, ne l'attendez pas."
                  : "Le copilote réfléchit…"}
              </div>
            )}
          </div>

          {/* BAS DU MILIEU — la seule chose à lire : suggestion, ou phrase de
              temporisation ; puis le pilotage. Borné pour ne jamais écraser la
              conversation. */}
          <div className="max-h-[58%] shrink-0 overflow-y-auto border-t border-[#eef0f4]">
            {/* AVERTISSEMENT — le Copilot dégradé se dit, il ne se cache pas */}
            {warning && !suggestion && (
              <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl bg-[#fdf3dd] px-3 py-2 text-[12.5px] text-[#8a6b1f]">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{warning.detail || "Copilote indisponible — l'appel continue normalement."}</span>
              </div>
            )}

            {/* PHRASE DE TEMPORISATION — visible tant qu'aucune phrase n'est
                proposée : c'est elle qu'on dit pendant que le copilote cherche,
                le client n'entend jamais de blanc (Lot 0). */}
            {live && !suggestion && (
              <div data-testid="copilot-tempo" className="mx-4 mt-3 rounded-xl bg-[#f7f8fa] px-3.5 py-2.5">
                <span className="font-mono text-[10px] uppercase tracking-[.1em] text-[#8a90a3]">
                  Pendant un blanc, dites
                </span>
                <p className="mt-0.5 text-[13px] leading-relaxed text-[#4c5164]">
                  « {prepared?.tempo || TEMPO_DEFAULT} »
                </p>
              </div>
            )}

            {/* SUGGESTION — la phrase à dire d'abord, saisie d'un seul regard ;
                la justification descend en dessous, repliée, pour après. */}
            {suggestion && (
              <div data-testid="copilot-suggestion" className="mx-4 mt-3 rounded-xl border border-[#b9b1ff] bg-[#f8f7ff] p-3.5">
                <div className="mb-2 flex items-center gap-2">
                  <Sparkles size={14} className="text-[#4a3ae0]" />
                  <span className={`rounded-md px-1.5 py-0.5 font-mono text-[10.5px] uppercase tracking-[.06em] ${typeClass}`}>
                    {suggestion.style || typeLabel}
                  </span>
                  {suggestion.priority === "high" && (
                    <span className="font-mono text-[10.5px] text-[#d64a3a]">prioritaire</span>
                  )}
                  {suggestion.source === "prepared" && (
                    <span className="ml-auto font-mono text-[10.5px] text-[#1f7a48]" title="Écrite avant l'appel, affichée sans délai">
                      préparée
                    </span>
                  )}
                </div>
                <p className="mb-2 text-[16px] font-medium leading-snug text-[#16181d]">« {suggestion.text} »</p>
                {suggestion.reason && (
                  <details className="mb-2.5">
                    <summary className="cursor-pointer list-none text-[11.5px] text-[#8a90a3] hover:text-[#61667a]">
                      Pourquoi cette phrase
                    </summary>
                    <p className="mt-1 text-[12px] leading-relaxed text-[#71768a]">{suggestion.reason}</p>
                  </details>
                )}
                {/* Un seul geste (Lot 2) : il la dit, ou il l'ignore. Une autre
                    formulation se demande par le menu « Suggérer ». */}
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid="copilot-use" onClick={useSuggestion}
                    className="flex items-center gap-1.5 rounded-lg bg-[#4a3ae0] px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-[#3a2cc8]">
                    <Check size={13} /> Utiliser
                  </button>
                  <button onClick={ignore}
                    className="rounded-lg px-2.5 py-1.5 text-[12.5px] text-[#8a90a3] hover:text-[#4c5164]">
                    Ignorer
                  </button>
                </div>
              </div>
            )}

            {/* COMPTE RENDU DE FIN D'APPEL — sauf si rien n'a été dit */}
            {!live && !analysis && !silent && (
              <div className="mx-4 mt-3 flex items-center gap-2 rounded-xl border border-[#e6e8ee] bg-[#f7f8fa] px-3.5 py-3 text-[12.5px] text-[#61667a]">
                <RefreshCw size={13} className="animate-spin" />
                Analyse de l'appel en cours… le compte rendu s'affiche ici dans quelques secondes.
              </div>
            )}
            {analysis && !silent && (
              <div className="mx-4 mt-3 rounded-xl border border-[#e6e8ee] bg-[#f7f8fa] p-3.5">
                <div className="mb-1.5 flex items-center gap-2">
                  <MessageSquare size={14} className="text-[#4a3ae0]" />
                  <span className="text-[13px] font-medium">Compte rendu — {analysis.outcome_label}</span>
                </div>
                <p className="text-[12.5px] leading-relaxed text-[#61667a]">{analysis.summary}</p>
                {companyId && (
                  <button onClick={() => navigate(`/entreprises/${companyId}?onglet=conversations`)}
                    className="mt-2 text-[12.5px] font-medium text-[#4a3ae0] hover:underline">
                    Voir la conversation sur la fiche
                  </button>
                )}
              </div>
            )}

            {/* PILOTAGE — inutile une fois l'appel terminé */}
            {live && (
              <div className="relative px-4 py-3">
                {/* Interrupteur du copilote automatique. Le couper n'arrête ni la
                    transcription ni la détection : seule la génération se tait, et
                    le bouton « Suggérer » ci-dessous reste servi. Sous verrou
                    administrateur, l'interrupteur est figé : le serveur refuse la
                    bascule et réannonce l'état, on ne fait pas semblant. */}
                <div className="mb-2.5 flex items-center justify-between gap-3">
                  <label htmlFor="copilot-auto" className={`min-w-0 ${autoSuggestLocked ? "cursor-default" : "cursor-pointer"}`}>
                    <span className="block text-[12.5px] font-medium text-[#4c5164]">
                      Suggestions automatiques
                    </span>
                    <span className="block truncate text-[11.5px] text-[#8a90a3]">
                      {autoSuggestLocked
                        ? "Désactivées par l'administrateur — seul « Suggérer » reste disponible"
                        : autoSuggest
                          ? (topRule ? `Le copilote suit la conversation — playbook « ${topRule} » en tête`
                                     : "Le copilote suit la conversation et le playbook de l'équipe")
                          : "Coupé — seul « Suggérer » produit une suggestion"}
                    </span>
                  </label>
                  <button id="copilot-auto" type="button" role="switch" aria-checked={autoSuggest}
                    aria-disabled={autoSuggestLocked} disabled={autoSuggestLocked}
                    data-testid="copilot-auto-toggle" onClick={() => setAuto(!autoSuggest)}
                    title={autoSuggestLocked ? "Réglage verrouillé par l'administrateur"
                           : autoSuggest ? "Couper les suggestions automatiques"
                                         : "Activer les suggestions automatiques"}
                    className={`relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors
                                ${autoSuggest ? "bg-[#4a3ae0]" : "bg-[#d3d7e1]"}
                                ${autoSuggestLocked ? "cursor-not-allowed opacity-50" : ""}`}>
                    <span className={`absolute left-[3px] top-[3px] h-4 w-4 rounded-full bg-white
                                      transition-transform ${autoSuggest ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>

                {menuOpen && (
                  <div className="absolute bottom-full left-4 right-4 z-[1] mb-2 rounded-xl border border-[#e6e8ee] bg-white py-1 shadow-lg">
                    {intents.map((intent) => (
                      <button key={intent.id}
                        onClick={() => { send("suggest", { intent: intent.id }); setMenuOpen(false); }}
                        className="block w-full px-3.5 py-2 text-left text-[13px] text-[#4c5164] hover:bg-[#f7f8fa]">
                        {intent.label}
                      </button>
                    ))}
                  </div>
                )}
                <button data-testid="copilot-ask" onClick={() => setMenuOpen((v) => !v)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#b9b1ff] bg-[#f8f7ff] py-2.5 text-[13.5px] font-medium text-[#4a3ae0] hover:bg-[#f0eeff]">
                  <Lightbulb size={15} /> Suggérer
                  <ChevronDown size={14} className={menuOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
              </div>
            )}
          </div>
        </main>

        {/* ══ DROITE — questions & informations utilisables pendant l'appel.
            Tout est déjà écrit : le commercial lit, il ne cherche pas. */}
        <aside data-testid="copilot-briefcase"
          className="hidden w-[290px] shrink-0 flex-col gap-4 overflow-y-auto border-l border-[#eef0f4] bg-[#fafbfc] p-3 md:flex">

          {((prepared?.answers || []).length > 0 || objections.length > 0) && (
            <button type="button" data-testid="copilot-toggle-all" onClick={toggleAll}
              aria-pressed={allOpen}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-[#e6e8ee] bg-white py-1.5 text-[11.5px] font-medium text-[#61667a] hover:border-[#b9b1ff] hover:text-[#3a2cc8]">
              {allOpen ? <ChevronsDownUp size={13} /> : <ChevronsUpDown size={13} />}
              {allOpen ? "Tout replier" : "Tout déplier"}
            </button>
          )}

          <section>
            <AsideTitle icon={Sparkles}>Réponses préparées</AsideTitle>
            {(prepared?.answers || []).length === 0 ? (
              <p className="mt-1.5 px-1 text-[12px] text-[#9aa0b2]">
                {live ? "En préparation — elles s'affichent dès que la banque est prête." : "—"}
              </p>
            ) : (
              <div className="mt-1.5 space-y-1.5">
                {prepared.answers.map((a) => (
                  <details key={`${a.key}-${epoch}`} open={allOpen} data-testid="copilot-answer"
                    className="rounded-xl border border-[#e6e8ee] bg-white px-3 py-2">
                    <summary className="cursor-pointer list-none text-[12.5px] font-medium text-[#4c5164]">
                      {a.label}
                    </summary>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-[#16181d]">« {a.text} »</p>
                  </details>
                ))}
              </div>
            )}
          </section>

          {objections.length > 0 && (
            <section>
              <AsideTitle icon={BookOpen}>Objections — réponses éprouvées</AsideTitle>
              <div className="mt-1.5 space-y-1.5">
                {objections.map((o) => (
                  <details key={`${o.id}-${epoch}`} open={allOpen} data-testid="copilot-objection"
                    className="rounded-xl border border-[#e6e8ee] bg-white px-3 py-2">
                    <summary className="cursor-pointer list-none text-[12.5px] font-medium text-[#4c5164]">
                      « {o.objection} »
                    </summary>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-[#16181d]">« {o.response} »</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {known && (
            <section>
              <AsideTitle icon={Brain}>Ce que l'on sait du prospect</AsideTitle>
              <div className="mt-1.5 rounded-xl border border-[#e8e2f4] bg-[#faf8fd] px-3 py-2.5">
                {memory.summary && (
                  <p className="text-[12px] leading-relaxed text-[#5b4a78]">{memory.summary}</p>
                )}
                {(memory.key_points || []).length > 0 && (
                  <ul className="mt-1.5 space-y-1">
                    {memory.key_points.slice(0, 6).map((point, i) => (
                      <li key={i} className="text-[12px] leading-snug text-[#5b4a78]">• {point}</li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
        </aside>
      </div>

      {planKind && company && (
        <PlanActionModal company={company} initialKind={planKind}
          onClose={() => setPlanKind(null)} onSaved={() => setPlanKind(null)} />
      )}
    </FloatingWindow>
  );
}
